import { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './FJPreviewModal.module.less';
import FJModalClose from '../FJModalClose/FJModalClose';
import FJLocalStore from '../../../store/FJLocalStore';
import DownloadIcon from '../../../assets/svg/download.svg?react';
import DownloadBlackIcon from '../../../assets/svg/download_black.svg?react';
import ApplyIcon from '../../../assets/svg/apply.svg?react';
import EditIcon from '../../../assets/svg/edit.svg?react';
import CatalogIcon from '../../../assets/svg/catalog.svg?react';
import AddToMediaIcon from '../../../assets/svg/add-to-media.svg?react';
import CopyIcon from '../../../assets/svg/copy.svg?react';
import FJIconButton from '../../../common/components/FJIconButton/FJIconButton';
import FJConstant from '../../../constants/FJConstant';
import classNames from 'classnames';
import FJUtil from '../../../util/FJUtil';
import FJMessageStore from '../../../store/FJMessageStore';
import FJVerticalScroller from '../../../common/components/FJScroller/FJVerticalScroller';
import FJAppConstant from '../../../constants/FJAppConstant';
import FJImageItemPreview from '../../../common/components/FJImageItemPreview/FJImageItemPreview';
import FJVideoContent from '../../../common/components/FJVideoPlayer/FJVideoContent.jsx';
import FJImageItemIconBox from '../../../common/components/FJImageItemPreview/FJImageItemIconBox.jsx';
import FJImage from '../../../common/components/FJImage/FJImage.jsx';
import FJAudioPreview from './FJAudioPreview.jsx';
import Download from '../../../lib/download.js';
import ReloadIcon from '../../../assets/svg/reload.svg?react';

const FJPreviewModal = props => {
    const {
        appType,
        data = {},
        onClose,
        onDownload,
        onDelete,
        onEdit,
        addAsScene,
        addToMedia,
        shareToSociety,
        isActiveNext,
        isActivePrevious,
        switchPreview,
        addAsSceneModalBtuStatus,
        isApplyButton,
        onApply,
        active,
        isAudioGenerator,
        isVideoGeneration,
        isVideoRemoveBg,
        garment,
    } = props;
    const { imgUrl, originalUrl, type, textToImagePrompt, videoUrl, prompt, width, height, generationType, ratio } =
        data;
    const { audioUrl, coverUrl, lyrics, audioName } = data;

    const videoContentRef = useRef(null);
    const audioRef = useRef(null);
    const [stateIsDownloadLoading, setStateIsDownloadLoading] = useState(false);
    const [garmentImageVersion, setGarmentImageVersion] = useState('');
    const [garmentImageState, setGarmentImageState] = useState('');

    const infoList = useMemo(() => {
        return FJUtil.parseModalInfoList(data, appType);
    }, [data, appType]);

    const _copyTextToImagePrompt = () => {
        if (isVideoGeneration) {
            FJUtil.copyText(prompt);
        } else if (isAudioGenerator) {
            FJUtil.copyText(lyrics);
        } else {
            FJUtil.copyText(textToImagePrompt);
        }
        FJMessageStore.addMessage(FJLocalStore._('copied-successfully'));
    };

    const _downloadImage = async () => {
        try {
            const blob = await FJUtil.downloadImageCacheAvoidance(coverUrl);
            const fileName = FJUtil.getUseFileName(audioName, '.png');
            Download(blob, fileName, 'image/png');
        } catch (e) {
            FJMessageStore.addMessage(FJLocalStore._('failed-to-download-please-try-again'), 'error');
        }
    };

    const _onDownload = () => {
        setStateIsDownloadLoading(true);
        onDownload(() => {
            setStateIsDownloadLoading(false);
        });
    };

    const _onRefresh = () => {
        audioRef.current.reloadCover();
    };

    const isShowComparison =
        appType !== FJAppConstant.TEXT_TO_IMAGE &&
        appType !== FJAppConstant.IMAGE_EXTENDER &&
        appType !== FJAppConstant.IMAGE_ENHANCER &&
        appType !== FJAppConstant.PHOTO_RESTORATION &&
        appType !== FJAppConstant.PHOTO_COLORIZE &&
        appType !== FJAppConstant.VIDEO_REMOVE_BG;

    const isShowSlideCompare =
        appType === FJAppConstant.IMAGE_ENHANCER ||
        appType === FJAppConstant.PHOTO_RESTORATION ||
        appType === FJAppConstant.PHOTO_COLORIZE;

    const isShowDeleteIcon = type === FJConstant.HISTORY;

    const isShowEditIcon = appType === FJAppConstant.PHOTO_REMOVE_BG || appType === FJAppConstant.VIDEO_REMOVE_BG;

    const _garmentImageDidLoad = () => {
        setGarmentImageState('success');
    };

    const _garmentImageError = () => {
        setGarmentImageState('error');
    };

    const _retryLoadGarment = () => {
        setGarmentImageState('loading');
        setGarmentImageVersion(new Date().getTime().toString());
    };

    const _renderGarment = () => {
        const imageUrl = garmentImageVersion
            ? `${garment}${garment.indexOf('?') === -1 ? '?v=' : '&v='}${garmentImageVersion}`
            : garment;
        return (
            <div className={styles.garment_box}>
                <div className={styles.garment_title}>{FJLocalStore._('clothes')}</div>
                {garmentImageState === 'error' ? (
                    <div className={styles.img_box1}>
                        <ReloadIcon className={styles.retry1} onClick={_retryLoadGarment} />
                    </div>
                ) : (
                    <div className={styles.img_box1}>
                        <img
                            src={imageUrl}
                            className={styles.img1}
                            onLoad={_garmentImageDidLoad}
                            onError={_garmentImageError}
                        />
                    </div>
                )}
                <div className={styles.garment_line} />
            </div>
        );
    };

    const _renderButton = () => {
        if (window.isEditPage) {
            return (
                <>
                    <FJIconButton
                        className={classNames(styles.btn, styles.margin_bottom_10)}
                        textClassName={styles.btn_text}
                        icon={isApplyButton ? <ApplyIcon /> : <AddToMediaIcon />}
                        text={FJLocalStore._(isApplyButton ? 'apply' : 'add-to-media')}
                        onClick={isApplyButton ? onApply : addToMedia}
                        type={FJConstant.GRADIENT}
                        strokeColor={'#FFF'}
                    />
                    <FJIconButton
                        className={classNames(styles.btn, styles.margin_bottom_37)}
                        textClassName={styles.btn_text}
                        icon={<DownloadBlackIcon />}
                        text={FJLocalStore._('download')}
                        onClick={_onDownload}
                        isLoading={stateIsDownloadLoading}
                    />
                </>
            );
        } else {
            return (
                <>
                    <FJIconButton
                        className={classNames(styles.btn, styles.margin_bottom_10)}
                        textClassName={styles.btn_text}
                        icon={<DownloadIcon />}
                        text={FJLocalStore._('download')}
                        onClick={_onDownload}
                        type={FJConstant.GRADIENT}
                        isLoading={stateIsDownloadLoading}
                        strokeColor={'#FFF'}
                    />
                    <FJIconButton
                        className={classNames(styles.btn, styles.margin_bottom_37)}
                        textClassName={styles.btn_text}
                        icon={<EditIcon />}
                        text={FJLocalStore._('use-in-a-design')}
                        onClick={addAsScene}
                        isLoading={addAsSceneModalBtuStatus === FJConstant.LOADING}
                    />
                </>
            );
        }
    };

    const renderLeftContent = () => {
        if (isVideoRemoveBg) {
            return (
                <div className={styles.video_box} ref={videoContentRef}>
                    <FJVideoContent
                        url={videoUrl}
                        isLoop={true}
                        ratio={width / height}
                        style={{ position: 'absolute' }}
                        isShowTransparentBG={true}
                    />
                    <FJImageItemIconBox
                        onDelete={onDelete}
                        shareToSociety={shareToSociety}
                        isShowDeleteIcon={true}
                        isShowComparison={false}
                        imageLoadingError={false}
                        disabledComparison={true}
                        isComparisonActive={false}
                        isShowEditIcon={isShowEditIcon}
                        onEdit={onEdit}
                    />
                </div>
            );
        } else if (isVideoGeneration) {
            return (
                <div className={styles.video_box} ref={videoContentRef}>
                    <FJVideoContent url={videoUrl} isLoop={true} ratio={ratio} style={{ position: 'absolute' }} />
                    <FJImageItemIconBox
                        onDelete={onDelete}
                        shareToSociety={shareToSociety}
                        isShowDeleteIcon={true}
                        isShowComparison={false}
                        imageLoadingError={false}
                        disabledComparison={true}
                        isComparisonActive={false}
                    />
                </div>
            );
        } else if (isAudioGenerator) {
            return (
                <div className={styles.video_box}>
                    <FJAudioPreview audioUrl={audioUrl} coverUrl={coverUrl} active={active} ref={audioRef} />
                    <FJImageItemIconBox
                        onDelete={onDelete}
                        shareToSociety={shareToSociety}
                        isShowDeleteIcon={true}
                        isShowComparison={false}
                        imageLoadingError={false}
                        disabledComparison={true}
                        isComparisonActive={false}
                    />
                </div>
            );
        } else {
            return (
                <FJImageItemPreview
                    imgUrl={imgUrl}
                    originalUrl={originalUrl}
                    isShowDeleteIcon={isShowDeleteIcon}
                    isShowComparison={isShowComparison}
                    isShowSlideCompare={isShowSlideCompare}
                    onDelete={onDelete}
                    shareToSociety={shareToSociety}
                    isShowEditIcon={isShowEditIcon}
                    onEdit={onEdit}
                />
            );
        }
    };

    const _onClose = () => {
        onClose();
        if (audioRef && audioRef.current) {
            audioRef.current.reset();
        }
    };

    return (
        <div className={styles.box}>
            <FJModalClose onClick={_onClose} />
            <div
                className={classNames(
                    styles.catalog_icon_box,
                    styles.catalog_left,
                    !isActivePrevious ? styles.disable : '',
                )}
                onClick={() => {
                    switchPreview(false);
                }}
            >
                <CatalogIcon />
            </div>
            <div
                className={classNames(
                    styles.catalog_icon_box,
                    styles.catalog_right,
                    !isActiveNext ? styles.disable : '',
                )}
                onClick={() => {
                    switchPreview(true);
                }}
            >
                <CatalogIcon />
            </div>

            <div className={styles.content}>
                {renderLeftContent()}

                <div className={styles.right}>
                    {_renderButton()}

                    {textToImagePrompt && (
                        <>
                            <div className={styles.info_item_prompt}>
                                <div className={styles.info_title_box}>
                                    <span className={styles.info_title}>{FJLocalStore._('prompt')}</span>
                                    <div className={styles.info_title_icon} onClick={_copyTextToImagePrompt}>
                                        <CopyIcon />
                                    </div>
                                </div>
                                <FJVerticalScroller className={classNames(styles.info_content_prompt)}>
                                    {textToImagePrompt}
                                </FJVerticalScroller>
                            </div>
                            <div className={styles.line}></div>
                        </>
                    )}

                    {isVideoGeneration && (
                        <>
                            {generationType === 'iv' && (
                                <div className={styles.info_item_prompt}>
                                    <div className={styles.info_title_box}>
                                        <span className={styles.info_title}>{FJLocalStore._('image')}</span>
                                    </div>
                                    <FJImage
                                        imgSrc={originalUrl}
                                        boxClassName={styles.img_box}
                                        imgClassName={styles.img}
                                    />
                                </div>
                            )}
                            {prompt && (
                                <div className={styles.info_item_prompt}>
                                    <div className={styles.info_title_box}>
                                        <span className={styles.info_title}>{FJLocalStore._('prompt')}</span>
                                        <div className={styles.info_title_icon} onClick={_copyTextToImagePrompt}>
                                            <CopyIcon />
                                        </div>
                                    </div>
                                    <FJVerticalScroller className={classNames(styles.info_content_prompt)}>
                                        {prompt}
                                    </FJVerticalScroller>
                                </div>
                            )}
                            <div className={styles.line}></div>
                        </>
                    )}

                    {isAudioGenerator && (
                        <div className={styles.info_item_prompt}>
                            <div className={styles.info_title_box}>
                                <span className={styles.info_title}>{FJLocalStore._('cover')}</span>
                                <div className={styles.info_title_icon} onClick={_downloadImage}>
                                    <DownloadBlackIcon />
                                </div>
                            </div>
                            <FJImage
                                imgSrc={coverUrl}
                                boxClassName={styles.img_box}
                                imgClassName={styles.img}
                                onRefresh={_onRefresh}
                                isShowLoading={true}
                            />
                        </div>
                    )}

                    {garment && _renderGarment()}

                    {infoList.map((item, index) => (
                        <div className={styles.info_item} key={index}>
                            <span className={styles.info_title}>{FJLocalStore._(item.title)}</span>
                            <span className={styles.info_content}>{item.content}</span>
                        </div>
                    ))}

                    {isAudioGenerator && lyrics && (
                        <>
                            <div className={styles.line} style={{ flex: 'none' }}></div>
                            <div className={styles.info_item_prompt} style={{ flex: 1, overflow: 'hidden' }}>
                                <div className={styles.info_title_box}>
                                    <span className={styles.info_title}>{FJLocalStore._('your-lyrics')}</span>
                                    <div className={styles.info_title_icon} onClick={_copyTextToImagePrompt}>
                                        <CopyIcon />
                                    </div>
                                </div>
                                <FJVerticalScroller className={classNames(styles.info_lyric)}>
                                    {lyrics}
                                </FJVerticalScroller>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
FJPreviewModal.propTypes = {
    appType: PropTypes.string,
    data: PropTypes.object,
    onClose: PropTypes.func,
    onDownload: PropTypes.func,
    onDelete: PropTypes.func,
    addAsScene: PropTypes.func,
    shareToSociety: PropTypes.func,
    isActiveNext: PropTypes.bool,
    isActivePrevious: PropTypes.bool,
    switchPreview: PropTypes.func,
    addAsSceneModalBtuStatus: PropTypes.string,
    onEdit: PropTypes.func,
    addToMedia: PropTypes.func,
    onApply: PropTypes.func,
    active: PropTypes.bool,
    isApplyButton: PropTypes.bool,
    isAudioGenerator: PropTypes.bool,
    isVideoGeneration: PropTypes.bool,
    isVideoRemoveBg: PropTypes.bool,
    garment: PropTypes.bool,
};
export default FJPreviewModal;
